import { HttpClient } from '@angular/common/http';
import { DestroyRef, Directive, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { FuseNavigationItem, FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { InitialData } from 'app/app.types';
import { AuthService } from 'app/core/auth/auth.service';
import { LayoutPanel, NavigationResponse } from 'app/layout/layout.types';
import { webpages_Role } from 'app/shared/models/domain/data.model';
import { environment } from 'environments/environment';

const DEFAULT_MODULE_TITLE = 'Title';
const DEFAULT_MODULE_ICON = 'mat_outline:admin_panel_setting';

/**
 * One entry of a layout's navigation chain: when the signed-in user belongs to
 * `roleKey`, these menus are shown. The first matching entry wins, so order
 * within `navigationChain` is significant.
 */
export interface LayoutNavigationEntry {
    roleKey: string;
    horizontal: FuseNavigationItem[];
    compact: FuseNavigationItem[];
    currentRole?: string;
}

/**
 * Shared state and route/navigation wiring for the vertical layouts
 * (classic, thin, dense). Subclasses supply their own template and a
 * `navigationChain` mapping role buckets onto the navigation menus.
 */
@Directive()
export abstract class VerticalLayoutBaseComponent implements OnInit {
    data: InitialData;
    isScreenSmall: boolean;
    panels: LayoutPanel[];
    drawerOpened: boolean = true;
    drawerMode: 'over' | 'side' = 'side';
    moduleTitle: string;
    moduleIcon: string;
    currentRole: string;
    selectedPanel: string;

    protected readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
    protected readonly router: Router = inject(Router);
    protected readonly authService: AuthService = inject(AuthService);
    protected readonly destroyRef: DestroyRef = inject(DestroyRef);
    private readonly fuseMediaWatcherService: FuseMediaWatcherService = inject(FuseMediaWatcherService);
    private readonly fuseNavigationService: FuseNavigationService = inject(FuseNavigationService);
    private readonly httpClient: HttpClient = inject(HttpClient);

    get currentYear(): number {
        return new Date().getFullYear();
    }

    ngOnInit(): void {
        this.currentRole = 'Admin';
        const roleKeys = this.resolveRoleKeys();

        this.activatedRoute.data
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: Data) => {
                this.data = data.initialData;
                this.httpClient.get<NavigationResponse>('api/common/navigation')
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((navigation: NavigationResponse) => {
                        this.applyNavigation(roleKeys, navigation);
                    });
                this.processRoute();
            });

        this.fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ matchingAliases }) => {
                this.onMediaChange(matchingAliases);
            });

        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.processRoute();
            });
    }

    toggleNavigation(name: string): void {
        const navigation = this.fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);
        if (navigation) {
            navigation.toggle();
        }
    }

    trackByFn(index: number, item: LayoutPanel): string | number {
        return item.id || index;
    }

    /**
     * Role buckets this layout can render, most specific first. Anything not
     * matched falls back to the default menus.
     */
    protected abstract navigationChain(navigation: NavigationResponse): LayoutNavigationEntry[];

    /** Applies the breakpoint flags this layout cares about. */
    protected onMediaChange(matchingAliases: string[]): void {
        this.isScreenSmall = !matchingAliases.includes('md');
    }

    private applyNavigation(roleKeys: string[], navigation: NavigationResponse): void {
        const match = this.navigationChain(navigation)
            .find((entry: LayoutNavigationEntry) => roleKeys.includes(entry.roleKey));

        if (!match) {
            this.data.navigation.horizontal = navigation.default;
            this.data.navigation.compact = navigation.compact;
            return;
        }

        this.data.navigation.horizontal = match.horizontal;
        this.data.navigation.compact = match.compact;
        if (match.currentRole) {
            this.currentRole = match.currentRole;
        }
    }

    /** `environment.roleTabs` keys the signed-in user belongs to. */
    private resolveRoleKeys(): string[] {
        const roleKeys: string[] = [];
        this.authService.localUserRoles
            ?.forEach((role: webpages_Role) => {
                Object.keys(environment.roleTabs)
                    .forEach((key: string) => {
                        if (environment.roleTabs[key].includes(role.roleName)
                            && !roleKeys.includes(key)) {
                            roleKeys.push(key);
                        }
                    });
            });
        return roleKeys;
    }

    /** Reads the panel drawer configuration off the deepest active route. */
    private processRoute(): void {
        this.panels = null;
        let route = this.activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        const candidates: Data[] = [route?.snapshot?.data, route?.parent?.snapshot?.data];
        for (const data of candidates) {
            if (data?.panels === false) {
                break;
            }
            if (data?.panels) {
                this.panels = data.panels;
                this.moduleTitle = data.moduleTitle || DEFAULT_MODULE_TITLE;
                this.moduleIcon = data.moduleIcon || DEFAULT_MODULE_ICON;
                this.drawerOpened = true;
                return;
            }
        }

        this.drawerOpened = false;
    }
}
